import { Box, Collapse, Typography } from '@mui/material'
import { ChevronDownIcon, ChevronRightIcon } from '~components/icons'
import type {
  GroupedPortInfo,
  EntryInfo,
  ContinentGroup,
  CountryGroup,
} from '../utils/group-port-info'
import CircularProgress from '@mui/material/CircularProgress'
import { COLORS } from '~theme/colors'
import { useDateFormat } from '~hooks/use-date-format'
import { formatDate } from '~utils/dates'
import { ExpandableGroupRow, ChildItemRow } from './shared'

type CategoryType = 'continents' | 'countries' | 'ports'

interface PortListProps {
  groupedData: GroupedPortInfo
  isLoading: boolean
  expandedCategories: CategoryType[]
  activeCategory: CategoryType | null
  selectedPortName: string | null
  selectedTerminal: string | null
  selectedContinentCode: string | null
  selectedCountryCode: string | null
  selectedEntryId: string | null
  onSelectCategory: (category: CategoryType) => void
  onSelectPort: (portName: string) => void
  onSelectTerminal: (terminalName: string) => void
  onSelectContinent: (code: string) => void
  onSelectCountry: (code: string) => void
  onSelectEntry: (id: string, category: 'continents' | 'countries') => void
}

export function PortList({
  groupedData,
  isLoading,
  expandedCategories,
  activeCategory,
  selectedPortName,
  selectedTerminal,
  // TODO: Re-enable when continents feature is needed
  selectedContinentCode: _selectedContinentCode,
  selectedCountryCode,
  selectedEntryId,
  onSelectCategory,
  onSelectPort,
  onSelectTerminal,
  // TODO: Re-enable when continents feature is needed
  onSelectContinent: _onSelectContinent,
  onSelectCountry,
  onSelectEntry,
}: PortListProps) {
  const datePattern = useDateFormat('literal')
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          minHeight: 200,
        }}
      >
        <CircularProgress aria-label="Loading…" />
      </Box>
    )
  }

  const { continents, countries, ports } = groupedData
  // TODO: Re-enable continents when needed
  const _continents = continents // Keep reference for future use
  const hasVisibleData = countries.length > 0 || ports.length > 0
  const hasHiddenContinentData = continents.length > 0 && !hasVisibleData

  if (!hasVisibleData && !hasHiddenContinentData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="paragraph2" color={COLORS.greys.dark}>
          No port info found
        </Typography>
      </Box>
    )
  }

  if (hasHiddenContinentData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="paragraph2" color={COLORS.greys.dark}>
          No port or country info found
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* TODO: Re-enable Continents section when needed
      <CategorySection
        title="Continents"
        count={continents.length}
        isExpanded={expandedCategories.includes('continents')}
        isActive={activeCategory === 'continents'}
        onToggle={() => onSelectCategory('continents')}
      >
        {continents.map((continent) => (
          <EntryGroupItem
            key={continent.continentCode}
            group={continent}
            groupName={continent.continentName}
            isExpanded={selectedContinentCode === continent.continentCode}
            isSelected={
              selectedContinentCode === continent.continentCode &&
              activeCategory === 'continents'
            }
            onSelectGroup={() => onSelectContinent(continent.continentCode)}
            entries={continent.entries}
            selectedEntryId={selectedEntryId}
            activeCategory={activeCategory}
            categoryType="continents"
            onSelectEntry={onSelectEntry}
            datePattern={datePattern}
          />
        ))}
      </CategorySection>
      */}

      <CategorySection
        className="tour-step-port-info-countries-section"
        title="Countries"
        count={countries.length}
        isExpanded={expandedCategories.includes('countries')}
        isActive={activeCategory === 'countries'}
        onToggle={() => onSelectCategory('countries')}
      >
        {countries.map((country) => (
          <EntryGroupItem
            key={country.countryCode}
            group={country}
            groupName={country.countryName}
            isExpanded={selectedCountryCode === country.countryCode}
            isSelected={
              selectedCountryCode === country.countryCode &&
              activeCategory === 'countries'
            }
            onSelectGroup={() => onSelectCountry(country.countryCode)}
            entries={country.entries}
            selectedEntryId={selectedEntryId}
            activeCategory={activeCategory}
            categoryType="countries"
            onSelectEntry={onSelectEntry}
            datePattern={datePattern}
          />
        ))}
      </CategorySection>

      <CategorySection
        className="tour-step-port-info-ports-section"
        title="Ports"
        count={ports.length}
        isExpanded={expandedCategories.includes('ports')}
        isActive={activeCategory === 'ports'}
        onToggle={() => onSelectCategory('ports')}
      >
        {ports.map((port) => {
          const isPortExpanded = selectedPortName === port.portName
          const isSelected = isPortExpanded && activeCategory === 'ports'

          return (
            <ExpandableGroupRow
              key={port.portName}
              label={
                <>
                  {port.portName}
                  <Typography component="span" variant="caption">
                    {' '}
                    ({port.portCode}) - {port.portCountry}
                  </Typography>
                </>
              }
              isExpanded={isPortExpanded}
              isSelected={isSelected}
              onClick={() => onSelectPort(port.portName)}
            >
              {port.terminals.map((terminal) => {
                const isTerminalSelected =
                  selectedTerminal === terminal.terminalName &&
                  activeCategory === 'ports'
                return (
                  <ChildItemRow
                    key={terminal.terminalName}
                    isSelected={isTerminalSelected}
                    onClick={() => onSelectTerminal(terminal.terminalName)}
                  >
                    <Typography variant="caption">
                      {terminal.terminalName}
                    </Typography>
                  </ChildItemRow>
                )
              })}
            </ExpandableGroupRow>
          )
        })}
      </CategorySection>
    </Box>
  )
}

interface EntryGroupItemProps {
  group: ContinentGroup | CountryGroup
  groupName: string
  isExpanded: boolean
  isSelected: boolean
  onSelectGroup: () => void
  entries: EntryInfo[]
  selectedEntryId: string | null
  activeCategory: CategoryType | null
  categoryType: 'continents' | 'countries'
  onSelectEntry: (id: string, category: 'continents' | 'countries') => void
  datePattern: string
}

function EntryGroupItem({
  groupName,
  isExpanded,
  isSelected,
  onSelectGroup,
  entries,
  selectedEntryId,
  activeCategory,
  categoryType,
  onSelectEntry,
  datePattern,
}: EntryGroupItemProps) {
  return (
    <ExpandableGroupRow
      label={
        <>
          {groupName}
          <Typography component="span" variant="caption">
            {' '}
            ({entries.length})
          </Typography>
        </>
      }
      isExpanded={isExpanded}
      isSelected={isSelected}
      onClick={onSelectGroup}
    >
      {entries.map((entry) => {
        const isEntrySelected =
          selectedEntryId === entry.entryId && activeCategory === categoryType
        return (
          <ChildItemRow
            key={entry.entryId}
            isSelected={isEntrySelected}
            onClick={() => onSelectEntry(entry.entryId, categoryType)}
          >
            <Typography variant="caption">
              {entry.subject || 'Entry'}
              {entry.dateOfInformation && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    fontStyle: 'italic',
                    color: COLORS.greys.mid,
                    ml: 0.5,
                  }}
                >
                  ({formatDate(entry.dateOfInformation, datePattern)})
                </Typography>
              )}
            </Typography>
          </ChildItemRow>
        )
      })}
    </ExpandableGroupRow>
  )
}

interface CategorySectionProps {
  title: string
  count: number
  isExpanded: boolean
  isActive: boolean
  onToggle: () => void
  children: React.ReactNode
  className?: string
}

function CategorySection({
  title,
  count,
  isExpanded,
  isActive,
  onToggle,
  children,
  className,
}: CategorySectionProps) {
  return (
    <Box>
      <Box
        className={className}
        onClick={onToggle}
        sx={{
          p: 2,
          cursor: 'pointer',
          borderBottom: 1,
          borderColor: COLORS.greys.light,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: isActive ? COLORS.secondary.darkBlue : 'transparent',
          color: isActive ? COLORS.primary.white : 'inherit',
          '&:hover': {
            bgcolor: isActive
              ? COLORS.secondary.darkBlue
              : COLORS.greys.ultraLight,
          },
        }}
      >
        <Box sx={{ color: 'inherit' }}>
          {isExpanded ? (
            <ChevronDownIcon width={16} height={16} />
          ) : (
            <ChevronRightIcon width={16} height={16} />
          )}
        </Box>
        <Typography variant="paragraphBold1" sx={{ color: 'inherit' }}>
          {title}
          <Typography
            component="span"
            variant="paragraph2"
            sx={{ color: 'inherit' }}
          >
            {' '}
            ({count})
          </Typography>
        </Typography>
      </Box>

      <Collapse in={isExpanded}>
        <Box>{children}</Box>
      </Collapse>
    </Box>
  )
}
